import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { createPortal } from "react-dom";

const NotificationContext = createContext({
  notify: () => {},
});

const TYPE_STYLES = {
  info: {
    badge: "bg-slate-900 text-white",
    border: "border-slate-200",
  },
  success: {
    badge: "bg-emerald-500 text-white",
    border: "border-emerald-200",
  },
  warning: {
    badge: "bg-amber-500 text-white",
    border: "border-amber-200",
  },
  error: {
    badge: "bg-rose-500 text-white",
    border: "border-rose-200",
  },
};

let counter = 0;

export const NotificationProvider = ({ children }) => {
  const [items, setItems] = useState([]);

  const remove = useCallback((id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const notify = useCallback(
    ({ title, message, type = "info", duration = 5000 }) => {
      const id = ++counter;
      const entry = {
        id,
        title: title || "",
        message: message || "",
        type,
      };
      setItems((prev) => [...prev, entry]);

      if (duration !== null) {
        window.setTimeout(() => {
          remove(id);
        }, duration);
      }

      return id;
    },
    [remove]
  );

  return (
    <NotificationContext.Provider value={{ notify }}>
      {children}
      <NotificationPortal notifications={items} onDismiss={remove} />
    </NotificationContext.Provider>
  );
};

const NotificationPortal = ({ notifications, onDismiss }) => {
  const [mountNode, setMountNode] = useState(null);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const el = document.createElement("div");
    document.body.appendChild(el);
    setMountNode(el);

    return () => {
      document.body.removeChild(el);
    };
  }, []);

  if (!mountNode) return null;

  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-[9999] flex flex-col items-end gap-3 px-4 py-6 sm:px-6">
      {notifications.map((note) => {
        const style = TYPE_STYLES[note.type] || TYPE_STYLES.info;
        return (
          <div
            key={note.id}
            className={[
              "pointer-events-auto w-full max-w-sm rounded-2xl border bg-white/95 p-4 shadow-2xl backdrop-blur",
              style.border,
            ].join(" ")}
          >
            <div className="flex items-start gap-3">
              <div
                className={[
                  "flex h-9 w-9 items-center justify-center rounded-2xl text-sm font-semibold",
                  style.badge,
                ].join(" ")}
              >
                {note.type === "error" && "!"}
                {note.type === "warning" && "!"}
                {note.type === "success" && "✓"}
                {note.type === "info" && "i"}
              </div>
              <div className="flex-1 text-sm text-slate-700">
                {note.title && (
                  <div className="font-semibold text-slate-900">
                    {note.title}
                  </div>
                )}
                {note.message && (
                  <div className="mt-1 text-xs text-slate-600">
                    {note.message}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => onDismiss(note.id)}
                className="rounded-full p-1 text-xs text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              >
                ✕
              </button>
            </div>
          </div>
        );
      })}
    </div>,
    mountNode
  );
};

export const useNotifications = () => useContext(NotificationContext);
